var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import SvgChevronRight from '@itwin/itwinui-icons-react/cjs/icons/ChevronRight';
import cx from 'classnames';
import React from 'react';
import { useTheme, StatusIconMap, WithCSSTransition, } from '../utils';
import '@itwin/itwinui-css/css/expandable-block.css';
/**
 * Container that allows content to be hidden behind a brief title and a caption.
 * @example
 * <ExpandableBlock title='Basic Block'>Content in block!</ExpandableBlock>
 * <ExpandableBlock title='Basic Block' caption='basic caption'>Content in block!</ExpandableBlock>
 * <ExpandableBlock title='Default Expanded Block' caption='basic caption' isExpanded={true}>Content in block!</ExpandableBlock>
 * <ExpandableBlock title='Positive status' status='positive'>Content</ExpandableBlock>
 * <ExpandableBlock title='Block with icon' endIcon={<SvgPlaceholder />}>Content</ExpandableBlock>
 */
export var ExpandableBlock = function (props) {
    var _a;
    var caption = props.caption, children = props.children, className = props.className, title = props.title, onToggle = props.onToggle, style = props.style, _b = props.isExpanded, isExpanded = _b === void 0 ? false : _b, endIcon = props.endIcon, status = props.status, _c = props.size, size = _c === void 0 ? 'default' : _c, _d = props.styleType, styleType = _d === void 0 ? 'default' : _d, rest = __rest(props, ["caption", "children", "className", "title", "onToggle", "style", "isExpanded", "endIcon", "status", "size", "styleType"]);
    useTheme();
    var icon = endIcon !== null && endIcon !== void 0 ? endIcon : (status && StatusIconMap[status]());
    var _e = React.useState(isExpanded), expanded = _e[0], setExpanded = _e[1];
    React.useEffect(function () {
        setExpanded(isExpanded);
    }, [isExpanded]);
    var handleToggle = function () {
        setExpanded(!expanded);
        onToggle === null || onToggle === void 0 ? void 0 : onToggle(!expanded);
    };
    var onKeyDown = function (event) {
        if (event.key === 'Enter' ||
            event.key === ' ' ||
            event.key === 'Spacebar') {
            handleToggle();
        }
    };
    return (React.createElement("div", __assign({ className: cx('iui-expandable-block', {
            'iui-with-caption': !!caption,
            'iui-expanded': expanded,
            'iui-small': size === 'small',
            'iui-borderless': styleType === 'borderless',
        }, className), style: style }, rest),
        React.createElement("div", { "aria-expanded": expanded, className: 'iui-header', tabIndex: 0, onClick: handleToggle, onKeyDown: onKeyDown },
            React.createElement(SvgChevronRight, { className: 'iui-icon', "aria-hidden": true }),
            React.createElement("span", { className: 'iui-expandable-block-label' },
                React.createElement("div", { className: 'iui-title' }, title),
                caption && React.createElement("div", { className: 'iui-caption' }, caption)),
            icon &&
                React.cloneElement(icon, {
                    className: cx('iui-status-icon', (_a = {}, _a["iui-".concat(status)] = !!status, _a), icon.props.className),
                })),
        React.createElement(WithCSSTransition, { in: expanded },
            React.createElement("div", { className: 'iui-expandable-content' },
                React.createElement("div", null, children)))));
};
export default ExpandableBlock;
